/**
 * =============================================================================
 * facets.js
 * =============================================================================
 * Collection / search filtering & sorting
 * 
 * See also:
 * - collection.js
 * - collection-grid.liquid
 * - facets.liquid
 * =============================================================================
 */

var Facets = {
  form: undefined, // The HTML form element of the facets form

  filterData: undefined, // Cache of previous filter view requests, so we only fetch a given filter view once
  searchParamsInitial: undefined,
  searchParamsPrev: undefined,
  sectionId: undefined, // ID of the results page section, so we know what section to request via fetch

  /**
   * Initialisation
   */
  init: function() {
    // Detect changes of radios within this element, as that means variant changed
    this.form = document.getElementById("facets-form");
    
    if(!this.form) {
      return;
    }

    this.filterData = [];
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.sectionId = document.getElementById("products-container").dataset.sectionId;

    this.setupEventListeners();
  },

  /*
   * Setup event listeners
   */
  setupEventListeners: function() {

    // Listen for form input changes - i.e. the facet inputs. We call the
    // listener after a 'debounce' period to allow the user some time to
    // complete their input (rather than immediately on input)
    this.debouncedOnInput = debounce((event) => {
      this.onFacetsFormInputChange(event);
    }, 500);

    this.form.addEventListener('input', this.debouncedOnInput.bind(this));

    this.form.addEventListener('change', function(event) {
      const input = document.querySelector('input[value="' + event.target.value  + '"]');
      input.checked = true;
    });

    // Listen for 'facet remove' clicks, trigger the load. We need to listen on 
    // the container, rather than directly on the buttons, as the buttons are
    // re-rendered / dynamically injected each time a filter is applied.
    this.form.addEventListener('click', function(event) {
      if(event.target.closest("a")?.classList.contains("facet-remove")){
        event.preventDefault();
        let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
        this.onActiveFilterClick(mockedEvent); // event has the wrong currentTarget - needs the 'a' element, but we're delegating
      };
    }.bind(this));
    
  },

  /**
   * Handle an input change in the new facets form for AB Testing, and render the new results
   * @param {Event} e input event 
   */
  onFacetsFormInputChange: function(e) {
    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams, e);
  },

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage: function(searchParams, event, updateURLHash = true) {

    this.searchParamsPrev = searchParams;
    document.getElementById('results-loading').classList.remove('d-none');
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    
    // Check if we have this combo of filters in the cache. If so, use cache. If
    // not, fetch fresh from Shopify.
    const filterDataUrl = element => element.url === url;
    this.filterData.some(filterDataUrl) ? 
      this.renderSectionFromCache(filterDataUrl, event) 
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  },

  /**
   * Render the section from previously-stored fetch (i.e. cache)
   * 
   * @param {String} filterDataUrl The URL we previously cached
   * @param {Event} event 
   */
  renderSectionFromCache: function(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.renderProductGridContainer(html);
    this.renderPagination(html);
    document.getElementById('results-loading').classList.add('d-none');
    mp_lazyload_setup();
    Waypoint.refreshAll();
  },

  /**
   * Render the section by fetching it fresh
   * 
   * @param {String} url 
   * @param {Event} event 
   */
  renderSectionFromFetch: function(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        // Cache this response so we can load directly if the same options are
        // picked.
        this.filterData = [...this.filterData, { html, url }];

        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        this.renderPagination(html);
        document.getElementById('results-loading').classList.add('d-none');
        mp_lazyload_setup();
        Waypoint.refreshAll();
      });
  },

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash: function(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  },

  /**
   * Extract and replace the facet (filter dropdowns) HTML
   * 
   * @param {String} html
   * @param {Event} event
   */
  renderFilters: function(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Update HTML of each facet that might have changed
    const facetsOld = parsedHTML.querySelectorAll('.facet-old');
    Array.from(facetsOld).forEach((element) => {
      document.querySelector(`.facet-old[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    const facetsNew = parsedHTML.querySelectorAll('.facet-new');
    Array.from(facetsNew).forEach((element) => {
      document.querySelector(`.facet-new[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    const resultCountElements = parsedHTML.querySelectorAll('.facet-result-count');    
    Array.from(resultCountElements).forEach((element) => {
      document.querySelector(`.facet-result-count`).innerHTML = element.innerHTML;
    });

    const resultCountElementsOld = parsedHTML.querySelectorAll('.facet-result-count-old');    
    Array.from(resultCountElementsOld).forEach((element) => {
      document.querySelector('.facet-result-count-old').innerHTML = element.innerHTML;
    });

    // Update HTML of the 'active filters' section
    this.renderActiveFacets(parsedHTML);
  },

  /**
   * Extract and replace the active facet (filter badges) HTML
   * 
   * @param {String} html
   */
  renderActiveFacets: function(html) {
    const activeFacetOldElementSelectors = ['#facets-active-old'];
    const activeFacetNewElementSelectors = ['#facets-active-new'];

    activeFacetOldElementSelectors.forEach((selector) => {
      const activeFacetsOldElement = html.querySelector(selector);
      if (!activeFacetsOldElement) return;
      document.querySelector(selector).outerHTML = activeFacetsOldElement.outerHTML;
    })

    activeFacetNewElementSelectors.forEach((selector) => {
      const activeFacetsNewElement = html.querySelector(selector);
      if (!activeFacetsNewElement) return;
      document.querySelector(selector).outerHTML = activeFacetsNewElement.outerHTML;
    })
    //FacetFiltersForm.toggleActiveFacets(false);
  },

  /**
   * Render the results products grid
   * 
   * @param {String} html
   */
  renderProductGridContainer: function(html) {
    document.getElementById('products-grid').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('products-grid').innerHTML;
  },

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html
   */
  renderPagination: function(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  },

  /**
   * Start the process of applying a filter
   * 
   * @param {Event} event The initiating event
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    //this.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url);
  }
}

export default Facets;